import type { ApplicationNinjaEquifaxScorePanelProps } from '../types/components'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { updateNinjaEquifaxScore } from '../services/applicationService'
import { readEquifaxScore, readNinjaRiskScore } from '../utils/ninjaEquifaxScoreFields'


export function ApplicationNinjaEquifaxScorePanel({
  applicantId,
  applicationData,
  onSaved,
}: ApplicationNinjaEquifaxScorePanelProps) {
  const [equifaxInput, setEquifaxInput] = useState('')
  const [ninjaInput, setNinjaInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false)

  const equifaxBaseline = readEquifaxScore(applicationData)
  const ninjaBaseline = readNinjaRiskScore(applicationData)

  useEffect(() => {
    setEquifaxInput(equifaxBaseline)
    setNinjaInput(ninjaBaseline)
  }, [equifaxBaseline, ninjaBaseline, applicationData.applicant_id])

  // Mirrors Angular `updateNinjaEquifaxScore` baseline diff:
  // include a field only when it is non-empty AND differs from the loaded baseline.
  const buildChangedPayload = (): {
    equifax_score?: string
    ninja_score?: string
  } => {
    const score = equifaxInput.trim()
    const riskScore = ninjaInput.trim()
    const initialEquifax = equifaxBaseline ? String(equifaxBaseline) : ''
    const initialNinja = ninjaBaseline ? String(ninjaBaseline) : ''

    const payload: { equifax_score?: string; ninja_score?: string } = {}
    if (score !== '' && score !== initialEquifax) payload.equifax_score = score
    if (riskScore !== '' && riskScore !== initialNinja) payload.ninja_score = riskScore
    return payload
  }

  const handleSubmitClick = () => {
    const payload = buildChangedPayload()
    if (Object.keys(payload).length === 0) {
      showError('No changes to save. Modify at least one score before submitting.')
      return
    }
    setSaveConfirmOpen(true)
  }

  const performSave = async () => {
    setSaveConfirmOpen(false)
    const payload = buildChangedPayload()
    if (Object.keys(payload).length === 0) {
      showError('No changes to save. Modify at least one score before submitting.')
      return
    }
    setSaving(true)
    try {
      await updateNinjaEquifaxScore(applicantId, payload)
      showSuccess('Score updated successfully')
      await onSaved()
    } catch (e: unknown) {
      showError(extractErrorMessage(e, 'Failed to update score'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="max-w-full rounded border border-[#d0d7de] bg-white px-3 py-2.5 sm:px-4"
      data-testid="application-detail-ninja-equifax-panel"
    >
      <div
        className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:gap-3"
        data-testid="application-detail-ninja-equifax-form"
      >
        <label
          className="flex min-w-[120px] flex-1 flex-col gap-0.5 text-[13px] font-medium text-[#232946]"
          data-testid="application-detail-ninja-equifax-equifax-label"
        >
          Score (Equifax)
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className="h-[34px] rounded border border-[#c5ccd3] bg-white px-2 text-sm text-[#232946] shadow-inner focus:border-[#1597ff] focus:outline-none focus:ring-1 focus:ring-[#1597ff]/25"
            data-testid="application-detail-ninja-equifax-equifax-input"
            value={equifaxInput}
            onChange={(e) => setEquifaxInput(e.target.value)}
          />
        </label>
        <label
          className="flex min-w-[120px] flex-1 flex-col gap-0.5 text-[13px] font-medium text-[#232946]"
          data-testid="application-detail-ninja-equifax-ninja-label"
        >
          Risk Score (Ninja)
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className="h-[34px] rounded border border-[#c5ccd3] bg-white px-2 text-sm text-[#232946] shadow-inner focus:border-[#1597ff] focus:outline-none focus:ring-1 focus:ring-[#1597ff]/25"
            data-testid="application-detail-ninja-equifax-ninja-input"
            value={ninjaInput}
            onChange={(e) => setNinjaInput(e.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={saving}
          className="inline-flex h-[34px] shrink-0 items-center justify-center rounded bg-[#1597ff] px-5 text-sm font-medium text-white shadow-[0_1px_3px_rgba(0,0,0,0.12)] hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:opacity-60 sm:mb-[1px] sm:self-end"
          data-testid="application-detail-ninja-equifax-submit-button"
          onClick={() => handleSubmitClick()}
        >
          {saving ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              Saving…
            </>
          ) : (
            'Submit'
          )}
        </button>
      </div>

      <ConfirmDialog
        isOpen={saveConfirmOpen}
        title="Update Ninja/Equifax Score"
        message="Do you want to update the score and risk score for this applicant?"
        primaryLabel="Confirm"
        secondaryLabel="Cancel"
        primaryFirst
        actionStyle="application"
        onPrimary={() => void performSave()}
        onSecondary={() => setSaveConfirmOpen(false)}
        onDismiss={() => setSaveConfirmOpen(false)}
        overlayDataTestId="application-detail-ninja-equifax-confirm-save-score"
        primaryButtonDataTestId="application-detail-ninja-equifax-confirm-save-score-primary-button"
        secondaryButtonDataTestId="application-detail-ninja-equifax-confirm-save-score-secondary-button"
        closeButtonDataTestId="application-detail-ninja-equifax-confirm-save-score-close-button"
      />
    </div>
  )
}
